<template>
  <div class="upload-view">
    <v-card class="upload-card mb-6">
      <div class="card-header">
        <div class="title-container">
          <v-icon color="primary" size="24" class="mr-2"
            >mdi-file-upload-outline</v-icon
          >
          <h2 class="card-title">Exceldaten importieren</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-content">
        <p class="upload-description mb-4">
          Füge hier die kopierten Daten aus Excel ein. Die Daten müssen
          zeilenweise und Tab-getrennt sein.
        </p>

        <v-textarea
          v-model="rawData"
          label="Excel-Daten"
          variant="outlined"
          bg-color="rgba(30, 30, 40, 0.5)"
          hide-details="auto"
          class="upload-textarea"
          color="success"
          rows="7"
          placeholder="Füge hier die Daten aus der Excel-Tabelle ein (STRG+V)"
          :disabled="isProcessing"
        >
          <template v-slot:prepend-inner>
            <v-icon color="success" size="small">mdi-table</v-icon>
          </template>
        </v-textarea>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-actions">
        <v-btn
          color="success"
          variant="elevated"
          class="parse-btn"
          :loading="isProcessing"
          :disabled="!rawData || isProcessing"
          :class="{ 'disabled-btn': !rawData || isProcessing }"
          @click="parseData"
        >
          <v-icon start>mdi-code-json</v-icon>
          Daten analysieren
        </v-btn>
      </div>
    </v-card>

    <v-expand-transition>
      <v-card v-if="parsedRows.length" class="upload-card preview-card mb-6">
        <div class="card-header">
          <div class="title-container">
            <v-icon color="primary" size="24" class="mr-2"
              >mdi-table-eye</v-icon
            >
            <h2 class="card-title">Vorschau der zu importierenden Daten</h2>
          </div>

          <div class="header-actions">
            <v-chip variant="elevated" color="success" class="status-counter">
              {{ selectedCount }} / {{ parsedRows.length }}
            </v-chip>
          </div>
        </div>

        <v-divider class="card-divider"></v-divider>

        <div class="card-content table-container">
          <div class="table-actions mb-3">
            <div class="selection-controls">
              <v-btn
                variant="text"
                size="small"
                color="success"
                class="selection-btn"
                @click="selectAll"
              >
                <v-icon start size="small">mdi-checkbox-marked-outline</v-icon>
                Alle auswählen
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                color="grey"
                class="selection-btn"
                @click="deselectAll"
              >
                <v-icon start size="small">mdi-checkbox-blank-outline</v-icon>
                Keine auswählen
              </v-btn>
            </div>

            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Suche"
              variant="outlined"
              density="compact"
              hide-details
              bg-color="rgba(30, 30, 40, 0.5)"
              class="search-field"
            ></v-text-field>
          </div>

          <v-data-table
            :headers="tableHeaders"
            :items="parsedRows"
            :search="search"
            item-key="index"
            class="data-table"
            density="comfortable"
          >
            <!-- Korrigierte v-slot Syntax -->
            <template v-slot:[`item.selected`]="{ item }">
              <v-checkbox
                v-model="item.selected"
                color="success"
                hide-details
              ></v-checkbox>
            </template>

            <template v-slot:[`item.timestamp`]="{ item }">
              {{ formatTimestamp(item.timestamp) }}
            </template>

            <template v-slot:[`item.typ`]="{ item }">
              <v-chip
                size="small"
                :color="getTypeColor(item.typ)"
                class="type-chip"
              >
                {{ item.typ }}
              </v-chip>
            </template>

            <template v-slot:bottom>
              <div class="text-center pt-2 pb-2">
                <p class="text-caption text-grey">
                  Bitte überprüfe die Daten vor dem Import sorgfältig.
                </p>
              </div>
            </template>
          </v-data-table>
        </div>

        <v-divider class="card-divider"></v-divider>

        <div class="card-actions">
          <v-btn
            color="error"
            variant="text"
            class="cancel-btn"
            :disabled="isUploading"
            :class="{ 'disabled-btn': isUploading }"
            @click="cancelImport"
          >
            <v-icon start>mdi-close</v-icon>
            Abbrechen
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            color="success"
            variant="elevated"
            class="save-btn"
            :loading="isUploading"
            :disabled="selectedCount === 0 || isUploading"
            :class="{ 'disabled-btn': selectedCount === 0 || isUploading }"
            @click="saveData"
          >
            <v-icon start>mdi-database-import</v-icon>
            Daten importieren
          </v-btn>
        </div>
      </v-card>
    </v-expand-transition>

    <!-- Keine Daten ausgewählt Dialog -->
    <v-dialog v-model="showNoDataDialog" max-width="400" class="error-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header error-header">
          <v-icon color="error" size="24" class="mr-2">mdi-alert-circle</v-icon>
          <span class="dialog-title">Keine Daten ausgewählt</span>
        </div>

        <v-divider class="card-divider"></v-divider>

        <div class="dialog-content">
          <p>Bitte wähle mindestens eine Zeile zum Importieren aus.</p>
        </div>

        <v-divider class="card-divider"></v-divider>

        <div class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="text"
            @click="showNoDataDialog = false"
          >
            Verstanden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";

const toast = useToast();

// State Management
const rawData = ref("");
const parsedRows = ref([]);
const search = ref("");
const isProcessing = ref(false);
const isUploading = ref(false);
const showNoDataDialog = ref(false);

// Computed Properties
const selectedCount = computed(() => {
  return parsedRows.value.filter((row) => row.selected).length;
});

// Table Configuration
const tableHeaders = [
  {
    title: "Übernehmen",
    key: "selected",
    align: "center",
    width: "80px",
    sortable: false,
  },
  { title: "Datum/Uhrzeit", key: "timestamp", align: "start" },
  { title: "Tätigkeit", key: "typ", align: "start" },
  { title: "Gerätetyp", key: "geraetetyp", align: "start" },
  { title: "Reparatur/Retourentyp", key: "reparaturtyp", align: "start" },
  { title: "Auftragsnummer", key: "auftragsnummer", align: "start" },
  { title: "Kommentar", key: "kommentar", align: "start" },
  { title: "Stückzahl", key: "menge", align: "center", width: "100px" },
];

// Helper Functions
const formatTimestamp = (ts) => {
  if (!ts) return "-";

  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  } catch (e) {
    return ts;
  }
};

const getTypeColor = (typ) => {
  if (!typ) return "grey";

  switch (typ.toLowerCase()) {
    case "reparatur":
      return "blue";
    case "retoure":
      return "amber";
    case "neugerät":
      return "success";
    default:
      return "grey";
  }
};

// Actions
const parseData = () => {
  if (!rawData.value.trim()) {
    toast.warning("Bitte füge zuerst Daten ein.");
    return;
  }

  isProcessing.value = true;
  parsedRows.value = [];

  try {
    const lines = rawData.value
      .split(/\r?\n/)
      .filter((line) => line.trim().length);

    if (lines.length === 0) {
      toast.warning("Keine Daten zum Verarbeiten gefunden.");
      isProcessing.value = false;
      return;
    }

    lines.forEach((line, index) => {
      const parts = line.split(/\t/);
      if (parts.length < 7) return;

      let rowTyp = parts[1].trim();
      let auftragsnummer, kommentar;
      let reparaturtypVal = "";
      let retourentypVal = "";

      // Verarbeitung von Retourendaten
      if (rowTyp.toLowerCase() === "retourenbearbeitung") {
        rowTyp = "Retoure";
        auftragsnummer = parts[5].trim();
        kommentar = parts[4].trim();
        retourentypVal = parts[3].trim();
      } else {
        auftragsnummer = parts[4].trim();
        kommentar = parts[5].trim();
        reparaturtypVal = parts[3].trim();
      }

      parsedRows.value.push({
        index,
        timestamp: parts[0].trim(),
        typ: rowTyp,
        geraetetyp: parts[2].trim(),
        reparaturtyp: reparaturtypVal,
        retourentyp: retourentypVal,
        auftragsnummer: auftragsnummer,
        kommentar: kommentar,
        menge: parts[6].trim(),
        selected: true,
      });
    });

    if (parsedRows.value.length === 0) {
      toast.error("Keine gültigen Daten gefunden. Bitte überprüfe das Format.");
    } else {
      toast.success(
        `${parsedRows.value.length} Datenzeilen erfolgreich analysiert.`
      );
    }
  } catch (error) {
    console.error("Fehler beim Parsen der Daten:", error);
    toast.error(
      "Fehler beim Verarbeiten der Daten. Bitte überprüfe das Format."
    );
  } finally {
    isProcessing.value = false;
  }
};

const saveData = async () => {
  const dataToUpload = parsedRows.value.filter((row) => row.selected);

  if (dataToUpload.length === 0) {
    showNoDataDialog.value = true;
    return;
  }

  isUploading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}data_upload.php`,
      { records: dataToUpload },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        `${dataToUpload.length} Datensätze erfolgreich importiert!`
      );
      parsedRows.value = [];
      rawData.value = "";
    } else {
      toast.error(
        `Fehler beim Import: ${response.data.message || "Unbekannter Fehler"}`
      );
    }
  } catch (error) {
    console.error("Fehler beim Upload:", error);
    toast.error(`Upload fehlgeschlagen: ${error.message || "Netzwerkfehler"}`);
  } finally {
    isUploading.value = false;
  }
};

const selectAll = () => {
  parsedRows.value.forEach((row) => (row.selected = true));
};

const deselectAll = () => {
  parsedRows.value.forEach((row) => (row.selected = false));
};

const cancelImport = () => {
  parsedRows.value = [];
  search.value = "";
};
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.upload-view {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Karten mit Glasmorphismus-Effekt ======== */
.upload-card,
.preview-card,
.dialog-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.upload-card:hover,
.preview-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

/* ======== Headers und Titel ======== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  z-index: 1;
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.dialog-title {
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.error-header {
  background: rgba(244, 67, 54, 0.1);
}

/* ======== Inhalts- und Aktionsbereiche ======== */
.card-content {
  padding: 24px;
}

.dialog-content {
  padding: 24px;
  color: rgba(255, 255, 255, 0.8);
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  background: rgba(20, 20, 30, 0.3);
}

.dialog-actions {
  padding: 12px 20px;
  background: rgba(20, 20, 30, 0.3);
}

/* ======== Eingabefelder ======== */
.upload-textarea {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 8px;
}

.upload-textarea :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.upload-textarea :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.upload-textarea :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.upload-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
}

/* ======== Aktionsbuttons ======== */
.parse-btn,
.save-btn {
  height: 44px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.parse-btn::before,
.save-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.6s;
}

.parse-btn:hover:not(:disabled),
.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.parse-btn:hover:not(:disabled)::before,
.save-btn:hover:not(:disabled)::before {
  left: 100%;
}

.cancel-btn {
  opacity: 0.85;
  transition: all 0.2s ease;
}

.cancel-btn:hover:not(:disabled) {
  opacity: 1;
  transform: translateY(-2px);
}

.header-actions {
  display: flex;
  align-items: center;
}

.status-counter {
  font-weight: 500;
  font-size: 0.85rem;
}

/* ======== Tabelle und Datenvorschau ======== */
.table-container {
  overflow-x: auto;
}

.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.selection-controls {
  display: flex;
  gap: 8px;
}

.selection-btn {
  letter-spacing: 0.3px;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.selection-btn:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
  transform: translateY(-1px);
}

.search-field {
  min-width: 240px;
  max-width: 320px;
}

.search-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.search-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.search-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.data-table {
  background: transparent !important;
  border-radius: 8px;
  overflow: hidden;
}

.data-table :deep(table) {
  background: transparent !important;
  border-spacing: 0;
}

.data-table :deep(tr:hover td) {
  background: rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(th) {
  background: rgba(20, 20, 25, 0.5) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
}

.data-table :deep(td) {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

/* Paginierung anpassen */
.data-table :deep(.v-data-table-footer) {
  background: rgba(20, 20, 25, 0.3) !important;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.data-table :deep(.v-data-table-footer__items-per-page) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-select .v-field) {
  background: rgba(30, 30, 40, 0.5) !important;
}

.data-table :deep(.v-data-table-footer .v-btn) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-btn--disabled) {
  color: rgba(255, 255, 255, 0.3) !important;
}

.type-chip {
  font-size: 0.8rem;
  font-weight: 500;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upload-card,
.preview-card {
  animation: fadeIn 0.5s ease-out;
}

/* ======== Deaktivierte Buttons ======== */
.disabled-btn {
  opacity: 0.65 !important;
  box-shadow: none !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.disabled-btn:hover {
  transform: none !important;
  box-shadow: none !important;
}

.disabled-btn::before {
  display: none;
}

/* ======== Responsive Design ======== */
@media (max-width: 960px) {
  .table-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .search-field {
    max-width: 100%;
  }

  .selection-controls {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .upload-view {
    padding-top: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
  }

  .header-actions {
    align-self: flex-end;
  }

  .card-content {
    padding: 16px;
  }

  .card-actions {
    padding: 12px 16px;
    flex-direction: column-reverse;
    gap: 12px;
  }

  .cancel-btn {
    width: 100%;
  }

  .save-btn {
    width: 100%;
  }

  .v-spacer {
    display: none;
  }
}

/* ======== Scrollbar-Styling ======== */
.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: transparent;
}

.table-container::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}
</style>
